import Box from "@/common/components/Box";
import TextField from "@/common/components/TextField";
import { UserData as UserDataType } from "@/common/types/users";
import { dateTransformer } from "@/common/utils/dateTransformer";
import { userStatusTranslation } from "@/common/constants/userStatus.constant";
import { formatCpf } from "@/common/utils/formatters";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import { UserRoundIcon } from "lucide-react";
import { GeoTransformers } from "@/common/services/geoTransformers";
import { GeoItem } from "@/common/types/geoGeoable";
import Select from "../Select";
import useUser from "@/common/hooks/useUser";
import { useState } from "react";
import { useRequestPasswordReset } from "@/features/users/hooks/useRequestPasswordReset";
import Modal from "../Modal";
import Button from "../Button";
import { ModalMessageSimple } from "../ModalMessageSimple";
import { authenticationMethodStrategy } from "@/features/users/constants/authenticationMethodOptions.constant";
import OAuthProviderField from '../OAuthProviderField';
import { UnlockUserAccess } from "@/common/components/UnlockUserAccess";
import { useQueryClient } from "@tanstack/react-query";
import useUserPermissions from "@/common/hooks/useUserPermissions";

interface UserDataProps {
  user: UserDataType | undefined;
}

const UserData = ({ user }: UserDataProps) => {
  const { data: userMe } = useUser({ id: 'me' });
  const queryClient = useQueryClient();

  const { userPermissionsSet } = useUserPermissions();

  const [isOpen, setIsOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const {
    mutate: requestPasswordReset,
    isPending: requestPasswordResetIsPending,
  } = useRequestPasswordReset({
    userId: user?.id,
    onSuccess: () => {
      setIsOpen(false);
      setIsError(false);
      setMessages(['Senha enviada ao usuário por e-mail e/ou SMS.']);
    },
    onError: (error) => {
      setIsOpen(false);
      setIsError(true);
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);
    },
  });

  return (
    <>
      <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] h-[calc((100vh-40px-10rem))] overflow-y-auto">
        {!!user?.failed_attempts && user?.failed_attempts > 0 && (
          <UnlockUserAccess
            userId={user?.id || ''}
            failedAttempts={user?.failed_attempts}
            lockedAt={user?.locked_at}
            entity=""
            operationKlassToCheckPermission="Admin::Users::UnlockUserAccess"
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['user', user?.id] })
            }}
          />
        )}

        <p className="text-lg font-bold">Dados pessoais</p>
        <div className="flex flex-col gap-2">
          <p className="text-xs">Foto</p>
          <ImageWithPlaceholder
            url={user?.profile_picture?.url}
            alt={user?.full_name}
            className="size-20"
            iconClassName="size-16"
            placeholderIcon={UserRoundIcon}
          />
        </div>
        <div className="flex gap-2 w-full justify-between">
          <TextField
            disabled
            label="CPF"
            className="w-3/4"
            value={user?.cpf ? formatCpf(user.cpf) : ''}
          />
          <TextField
            disabled
            label="Data de nascimento"
            className="w-1/4"
            value={user?.date_of_birth ? dateTransformer(user?.date_of_birth) : ''}
          />
        </div>
        <div className="flex gap-2 w-full justify-between">
          <TextField
            disabled
            label="Nome"
            className="w-3/4"
            value={user?.full_name || ''}
          />
          <TextField
            disabled
            label="Perfil"
            maxLength={32}
            className="w-1/4"
            value={user?.user_roles?.map((userRole) => userRole.role.display_name).join(', ')}
          />
        </div>
        <TextField
          disabled
          label="Status"
          value={userStatusTranslation[(user?.status || 'default') as keyof typeof userStatusTranslation]}
        />
        <Select
          isDisabled
          isMulti
          label="Região de atuação"
          value={GeoTransformers.mapToGeo()(user?.geo_geoables as GeoItem[])}
        />
        <TextField
          disabled
          label="Configuração de login"
          value={
            authenticationMethodStrategy[user?.authentication_method as keyof typeof authenticationMethodStrategy] || ''
          }
        />
        <TextField
          disabled
          label="Telefone"
          value={user?.phone?.number || ''}
        />
        <div className='flex gap-2 w-full items-end'>
          <TextField
            disabled
            label="Email"
            value={user?.email || ''}
            className='w-full'
          />

          <OAuthProviderField user={user} />
        </div>
        <TextField
          disabled
          label="Código SICOF"
          value={user?.sicof_user_identification || ''}
        />

        {user && userMe?.id !== user.id && userPermissionsSet.has('Admin::Users::RequestPasswordReset') && (
          <div className="w-full rounded-sm p-4 bg-white-100">
            <div className="flex w-full items-center justify-between">
              <p>Senha de acesso ao sistema de gestão TCard</p>

              <Modal
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                Trigger={
                  <Button disabled={requestPasswordResetIsPending}>
                    Redefinir senha
                  </Button>
                }
                useCancellableFooter
                showClose={false}
                title="Você deseja realmente alterar essa senha?"
                subtitle="Ao clicar em confirmar, a senha anterior do usuário será desativada."
                subtitleClassName="mt-1 text-md text-white-300"
                confirmButtonClassName="bg-blue-700"
                onConfirm={() => requestPasswordReset()}
              />
            </div>
          </div>
        )}
      </Box>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={isError}
        />
      )}
    </>
  );
};

export default UserData;
